import * as DocumentPicker from "expo-document-picker";
import * as FileSystem from "expo-file-system";
import * as SecureStore from "expo-secure-store";
import * as Sharing from "expo-sharing";
import { useContext, useEffect, useState } from "react";
import {
    ActivityIndicator,
    Alert,
    FlatList,
    Image,
    Linking,
    Modal,
    Pressable,
    StyleSheet,
    Text,
    TouchableOpacity,
    View,
} from "react-native";
import Icon from "react-native-vector-icons/MaterialCommunityIcons";
import PageTitle from "../../../../components/PageTitle";
import { GlobalContext } from "../../../../contexts/global";
import {
    DeleteDocument,
    GetMyDocuments,
    UploadDocument,
} from "../../../../services/Api/documents";
import { openCamera as openCameraPicker, openPicker as openGalleryPicker } from "./imagePicker";
import * as Colors from "../../../../styles/colors";
import * as Fonts from "../../../../styles/typography";
import { urlApi } from "../../../../services/Api";

const DOCUMENT_TYPES = [
    { label: "CNH", value: "CNH" },
    { label: "RG", value: "RG" },
    { label: "CRLV", value: "CRLV" },
    { label: "Comprovante de Residência", value: "Comprovante de Residência" },
    { label: "Carteirinha Universitária", value: "Carteirinha Universitária" },
    { label: "Declaração Escolar", value: "Declaração Escolar" },
    { label: "Histórico Escolar", value: "Histórico Escolar" },
    { label: "Outro", value: "Outro" },
];

// ⚠️  MOCK — altere para false antes de ir para produção
const USE_MOCK = true;
const MOCK_DOCUMENTS = [
    { id: 1, documentType: "CNH", fileName: "cnh_joao.pdf", dtCreated: "2026-02-20T10:00:00" },
    { id: 2, documentType: "CRLV", fileName: "crlv_gol.jpg", dtCreated: "2026-02-21T14:30:00" },
    { id: 3, documentType: "RG", fileName: "rg_frente.png", dtCreated: "2026-02-22T09:15:00" },
];

function typeIcon(type) {
    switch (type) {
        case "CNH":
            return "card-account-details-outline";
        case "RG":
            return "card-bulleted-outline";
        case "CRLV":
            return "car-info";
        case "Comprovante de Residência":
            return "home-outline";
        default:
            return "file-document-outline";
    }
}

function formatDate(dateStr) {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    return d.toLocaleDateString("pt-BR");
}

export default function MyDocuments({ navigation }) {
    const { setLoading, setShowAlert, showAlert } = useContext(GlobalContext);

    const [documents, setDocuments] = useState([]);
    const [refreshing, setRefreshing] = useState(false);
    const [viewerDoc, setViewerDoc] = useState(null);

    // Modal state
    const [modalVisible, setModalVisible] = useState(false);
    const [selectedType, setSelectedType] = useState(null);
    const [selectedFile, setSelectedFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [pendingAction, setPendingAction] = useState(null);
    const [userToken, setUserToken] = useState("");

    useEffect(() => {
        loadDocuments();
        SecureStore.getItemAsync("TokenUser").then(token => setUserToken(token || ""));
    }, []);

    useEffect(() => {
        if (!modalVisible && pendingAction) {
            const timer = setTimeout(() => {
                const action = pendingAction;
                setPendingAction(null); // limpa antes de chamar
                if (action === "camera") {
                    handleCamera();
                } else if (action === "file") {
                    handleFilePicker();
                }
            }, 600); // Aguarda o modal fechar completamente
            return () => clearTimeout(timer);
        }
    }, [modalVisible, pendingAction]);
    
    const loadDocuments = () => {
        if (USE_MOCK) {
            setDocuments(MOCK_DOCUMENTS);
            return;
        }
        setLoading({ show: true, message: "Carregando documentos" });
        GetMyDocuments()
            .then((data) => setDocuments(data))
            .catch(() =>
                setShowAlert({
                    type: "alert",
                    title: "Atenção",
                    message: "Não foi possível carregar seus documentos.",
                    show: true,
                    confirmText: "OK",
                    pressConfirm: () => setShowAlert({ ...showAlert, show: false }),
                })
            )
            .finally(() => setLoading({ show: false, message: "" }));
    };

    const handlePickSource = () => {
        Alert.alert(
            "Selecionar arquivo",
            "Como deseja adicionar o documento?",
            [
                {
                    text: "📷 Tirar foto",
                    onPress: () => {
                        setPendingAction("camera");
                        setModalVisible(false);
                    },
                },
                {
                    text: " Escolher arquivo (Foto ou PDF)",
                    onPress: () => {
                        setPendingAction("file");
                        setModalVisible(false);
                    },
                },
                { text: "Cancelar", style: "cancel" },
            ]
        );
    };

    const handleCamera = async () => {
        try {
            const image = await openCameraPicker();

            // Reabre o Modal após retornar da câmera
            setModalVisible(true);

            setSelectedFile({
                uri: image.path,
                name: `foto_${Date.now()}.jpg`,
                mimeType: image.mime || "image/jpeg",
            });
        } catch (error) {
            console.log(error);
            setModalVisible(true);
            const errStr = String(error);

            // Se o usuário apenas fechou a câmera / cancelou
            if (errStr.toLowerCase().includes("user cancelled") || errStr.toLowerCase().includes("cancelled") || errStr.includes("E_PICKER_CANCELLED")) {
                return;
            }

            // Exibir a causa real do problema
            let msgErro = "Não foi possível abrir a câmera.";
            if (errStr.includes("rejected permissions") || errStr.includes("rejected") || errStr.includes("E_NO_CAMERA_PERMISSION")) {
                msgErro = "Permissão negada. Acesse as Configurações do seu aparelho para liberar o acesso à Câmera.";
            } else {
                msgErro += " Detalhes: " + errStr;
            }

            setShowAlert({
                type: "alert",
                title: "Atenção",
                message: msgErro,
                show: true,
                confirmText: "OK",
                pressConfirm: () => setShowAlert({ ...showAlert, show: false }),
            });
        }
    };

    const handleGallery = async () => {
        try {
            const image = await openGalleryPicker();
            setSelectedFile({
                uri: image.path,
                name: `imagem_${Date.now()}.jpg`,
                mimeType: image.mime || "image/jpeg",
            });
        } catch (error) {
            const errStr = String(error);
            if (!errStr.toLowerCase().includes("cancelled") && !errStr.includes("E_PICKER_CANCELLED")) {
                setShowAlert({
                    type: "alert",
                    title: "Atenção",
                    message: "Não foi possível abrir a galeria.",
                    show: true,
                    confirmText: "OK",
                    pressConfirm: () => setShowAlert({ ...showAlert, show: false }),
                });
            }
        }
    };

    const handleFilePicker = async () => {
        try {
            const result = await DocumentPicker.getDocumentAsync({
                type: ["application/pdf", "image/jpeg", "image/png", "image/jpg"],
                copyToCacheDirectory: true,
            });
            setModalVisible(true);
            if (result.type === "success") {
                setSelectedFile({ name: result.name, uri: result.uri, mimeType: result.mimeType });
            } else if (!result.canceled && result.assets?.length > 0) {
                setSelectedFile(result.assets[0]);
            }
        } catch (error) {
            setModalVisible(true);
            setShowAlert({
                type: "alert",
                title: "Erro",
                message: "Não foi possível selecionar o arquivo.",
                show: true,
                confirmText: "OK",
                pressConfirm: () => setShowAlert({ ...showAlert, show: false }),
            });
        }
    };


    const handleUpload = () => {
        if (!selectedType) {
            setShowAlert({
                type: "info",
                title: "Atenção",
                message: "Selecione o tipo do documento.",
                show: true,
                confirmText: "OK",
                pressConfirm: () => setShowAlert({ ...showAlert, show: false }),
            });
            return;
        }
        if (!selectedFile) {
            setShowAlert({
                type: "info",
                title: "Atenção",
                message: "Selecione um arquivo para enviar.",
                show: true,
                confirmText: "OK",
                pressConfirm: () => setShowAlert({ ...showAlert, show: false }),
            });
            return;
        }

        if (USE_MOCK) {
            const newDoc = {
                id: Date.now(),
                documentType: selectedType,
                fileName: selectedFile.name,
                dtCreated: new Date().toISOString(),
                localUri: selectedFile.uri,
                mimeType: selectedFile.mimeType,
            };
            setDocuments((prev) => [newDoc, ...prev]);
            setModalVisible(false);
            setSelectedType(null);
            setSelectedFile(null);
            return;
        }

        setUploading(true);
        UploadDocument(
            selectedType,
            selectedFile.uri,
            selectedFile.name,
            selectedFile.mimeType || "application/octet-stream"
        )
            .then(() => {
                setModalVisible(false);
                setSelectedType(null);
                setSelectedFile(null);
                loadDocuments();
            })
            .catch(() =>
                setShowAlert({
                    type: "alert",
                    title: "Erro",
                    message: "Não foi possível enviar o documento. Tente novamente.",
                    show: true,
                    confirmText: "OK",
                    pressConfirm: () => setShowAlert({ ...showAlert, show: false }),
                })
            )
            .finally(() => setUploading(false));
    };

    const confirmDelete = (doc) => {
        setShowAlert({
            type: "warning",
            title: "Atenção",
            message: `Deseja remover o documento "${doc.documentType}"? Essa ação é irreversível.`,
            show: true,
            confirmText: "Sim, remover",
            cancelText: "Cancelar",
            pressConfirm: () => {
                setShowAlert({ ...showAlert, show: false });
                handleDelete(doc.id);
            },
            pressCancel: () => setShowAlert({ ...showAlert, show: false }),
        });
    };

    const handleDelete = (id) => {
        if (USE_MOCK) {
            setDocuments((prev) => prev.filter((d) => d.id !== id));
            return;
        }
        setLoading({ show: true, message: "Removendo documento" });
        DeleteDocument(id)
            .then(() => loadDocuments())
            .catch(() =>
                setShowAlert({
                    type: "alert",
                    title: "Erro",
                    message: "Não foi possível remover o documento.",
                    show: true,
                    confirmText: "OK",
                    pressConfirm: () => setShowAlert({ ...showAlert, show: false }),
                })
            )
            .finally(() => setLoading({ show: false, message: "" }));
    };

    const handleView = async (item) => {
        // Determina a URI de visualização
        const uri = item.localUri  // mock: URI local do arquivo selecionado
            ?? (item.url ? (item.url.startsWith("http") ? item.url : urlApi + item.url) : null);

        if (!uri) {
            setShowAlert({
                type: "info",
                title: "Indisponível",
                message: "Não é possível visualizar este documento.",
                show: true,
                confirmText: "OK",
                pressConfirm: () => setShowAlert({ show: false }),
            });
            return;
        }

        const mime = item.mimeType ?? item.contentType ?? "";
        if (mime.startsWith("image/")) {
            setViewerDoc({ uri, type: "image", title: item.documentType });
        } else {
            // PDF ou outro — abre com app nativo
            if (uri.startsWith("http") && userToken) {
                try {
                    setLoading({ show: true, message: "Baixando arquivo..." });
                    const ext = item.fileName ? item.fileName.split('.').pop() : "pdf";
                    const fileUri = FileSystem.documentDirectory + `documento_${item.id || Date.now()}.${ext}`;
                    
                    const result = await FileSystem.downloadAsync(uri, fileUri, {
                        headers: { Authorization: "Bearer " + userToken }
                    });
                    
                    setLoading({ show: false, message: "" });

                    if (await Sharing.isAvailableAsync()) {
                        await Sharing.shareAsync(result.uri, {
                            mimeType: mime || 'application/pdf',
                            dialogTitle: 'Visualizar Documento'
                        });
                    } else {
                        setShowAlert({
                            type: "info",
                            title: "Download Concluído",
                            message: "Arquivo salvo em: " + result.uri,
                            show: true,
                            pressConfirm: () => setShowAlert({ show: false })
                        });
                    }
                } catch (e) {
                    setLoading({ show: false, message: "" });
                    setShowAlert({
                        type: "alert",
                        title: "Erro",
                        message: "Não foi possível baixar o arquivo para visualização.",
                        show: true,
                        pressConfirm: () => setShowAlert({ show: false })
                    });
                }
            } else {
                Linking.openURL(uri).catch(() =>
                    setShowAlert({
                        type: "alert",
                        title: "Erro",
                        message: "Não foi possível abrir o arquivo.",
                        show: true,
                        confirmText: "OK",
                        pressConfirm: () => setShowAlert({ show: false }),
                    })
                );
            }
        }
    };

    const renderItem = ({ item }) => (
        <View style={styles.card}>
            <View style={styles.cardLeft}>
                <Icon
                    name={typeIcon(item.documentType)}
                    size={32}
                    color={Colors.PRIMARY ?? "#3B82F6"}
                    style={styles.cardIcon}
                />
                <View style={styles.cardInfo}>
                    <Text style={styles.cardType}>{item.documentType}</Text>
                    <Text style={styles.cardDate}>Enviado em {formatDate(item.dtCreated)}</Text>
                    <Text style={styles.cardFile} numberOfLines={1}>
                        {item.fileName}
                    </Text>
                </View>
            </View>
            <TouchableOpacity
                style={styles.viewBtn}
                onPress={() => handleView(item)}
                accessibilityLabel="Visualizar documento"
            >
                <Icon name="eye-outline" size={22} color={Colors.PRIMARY ?? "#3B82F6"} />
            </TouchableOpacity>
            <TouchableOpacity
                style={styles.deleteBtn}
                onPress={() => confirmDelete(item)}
                accessibilityLabel="Remover documento"
            >
                <Icon name="delete-outline" size={22} color={Colors.ALERT ?? "#EF4444"} />
            </TouchableOpacity>
        </View>
    );

    return (
        <PageTitle title="Meus Documentos" navigation={navigation}>
            {/* Document list */}
            <FlatList
                data={documents}
                keyExtractor={(item) => String(item.id)}
                renderItem={renderItem}
                onRefresh={loadDocuments}
                refreshing={refreshing}
                contentContainerStyle={
                    documents.length === 0 ? styles.emptyContainer : styles.list
                }
                ListEmptyComponent={
                    <View style={styles.emptyBox}>
                        <Icon
                            name="file-document-outline"
                            size={64}
                            color={Colors.GRAY ?? "#9CA3AF"}
                        />
                        <Text style={styles.emptyText}>Nenhum documento cadastrado</Text>
                        <Text style={styles.emptySubText}>
                            Adicione seus documentos pessoais abaixo
                        </Text>
                    </View>
                }
            />

            {/* Add button */}
            <TouchableOpacity
                style={styles.addButton}
                onPress={() => setModalVisible(true)}
                accessibilityLabel="Adicionar documento"
            >
                <Icon name="plus" size={20} color="#FFF" />
                <Text style={styles.addButtonText}>Adicionar Documento</Text>
            </TouchableOpacity>

            {/* Upload Modal */}
            <Modal
                visible={modalVisible}
                animationType="slide"
                transparent
                onRequestClose={() => setModalVisible(false)}
            >
                <Pressable
                    style={styles.modalOverlay}
                    onPress={() => setModalVisible(false)}
                >
                    <Pressable style={styles.modalBox} onPress={() => { }}>
                        <Text style={styles.modalTitle}>Novo Documento</Text>

                        {/* Type selector */}
                        <Text style={styles.label}>Tipo de Documento</Text>
                        <View style={styles.typeGrid}>
                            {DOCUMENT_TYPES.map((t) => (
                                <TouchableOpacity
                                    key={t.value}
                                    style={[
                                        styles.typeChip,
                                        selectedType === t.value && styles.typeChipSelected,
                                    ]}
                                    onPress={() => setSelectedType(t.value)}
                                >
                                    <Text
                                        style={[
                                            styles.typeChipText,
                                            selectedType === t.value && styles.typeChipTextSelected,
                                        ]}
                                    >
                                        {t.label}
                                    </Text>
                                </TouchableOpacity>
                            ))}
                        </View>

                        {/* File picker */}
                        <Text style={styles.label}>Arquivo</Text>
                        <TouchableOpacity style={styles.filePicker} onPress={handlePickSource}>
                            <Icon
                                name={selectedFile ? "file-check-outline" : "camera-plus-outline"}
                                size={22}
                                color={selectedFile ? Colors.SUCCESS ?? "#10B981" : Colors.GRAY ?? "#9CA3AF"}
                            />
                            <Text
                                style={[styles.filePickerText, selectedFile && styles.filePickerSelected]}
                                numberOfLines={1}
                            >
                                {selectedFile ? selectedFile.name : "Foto, galeria ou arquivo PDF…"}
                            </Text>
                        </TouchableOpacity>

                        {/* Actions */}
                        <View style={styles.modalActions}>
                            <TouchableOpacity
                                style={styles.cancelBtn}
                                onPress={() => {
                                    setModalVisible(false);
                                    setSelectedType(null);
                                    setSelectedFile(null);
                                }}
                            >
                                <Text style={styles.cancelBtnText}>Cancelar</Text>
                            </TouchableOpacity>
                            <TouchableOpacity
                                style={styles.confirmBtn}
                                onPress={handleUpload}
                                disabled={uploading}
                            >
                                {uploading ? (
                                    <ActivityIndicator color="#FFF" size="small" />
                                ) : (
                                    <Text style={styles.confirmBtnText}>Enviar</Text>
                                )}
                            </TouchableOpacity>
                        </View>
                    </Pressable>
                </Pressable>
            </Modal>

            {/* Image Viewer Modal */}
            <Modal
                visible={!!viewerDoc}
                animationType="fade"
                transparent
                onRequestClose={() => setViewerDoc(null)}
            >
                <Pressable style={styles.viewerOverlay} onPress={() => setViewerDoc(null)}>
                    <View style={styles.viewerHeader}>
                        <Text style={styles.viewerTitle}>{viewerDoc?.title}</Text>
                        <TouchableOpacity onPress={() => setViewerDoc(null)}>
                            <Icon name="close" size={28} color="#FFF" />
                        </TouchableOpacity>
                    </View>
                    {viewerDoc?.type === "image" && (
                        <Image
                            source={{
                                uri: viewerDoc.uri,
                                headers: userToken ? { Authorization: "Bearer " + userToken } : undefined
                            }}
                            style={styles.viewerImage}
                            resizeMode="contain"
                        />
                    )}
                </Pressable>
            </Modal>
        </PageTitle>
    );
}

const PRIMARY = Colors.PRIMARY ?? "#3B82F6";
const ALERT = Colors.ALERT ?? "#EF4444";
const GRAY = Colors.GRAY ?? "#9CA3AF";
const GRAY_DARK = Colors.GRAY_DARK ?? "#374151";

const styles = StyleSheet.create({
    list: {
        padding: 12,
    },
    emptyContainer: {
        flex: 1,
        justifyContent: "center",
    },
    emptyBox: {
        alignItems: "center",
        paddingVertical: 40,
    },
    emptyText: {
        marginTop: 12,
        fontSize: Fonts.FONT_SIZE_16 ?? 16,
        fontWeight: "600",
        color: GRAY_DARK,
    },
    emptySubText: {
        marginTop: 4,
        fontSize: Fonts.FONT_SIZE_14 ?? 14,
        color: GRAY,
        textAlign: "center",
    },
    card: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        backgroundColor: "#FFF",
        borderRadius: 12,
        padding: 14,
        marginBottom: 10,
        shadowColor: "#000",
        shadowOpacity: 0.06,
        shadowOffset: { width: 0, height: 2 },
        shadowRadius: 6,
        elevation: 3,
    },
    cardLeft: {
        flexDirection: "row",
        alignItems: "center",
        flex: 1,
        marginRight: 8,
    },
    cardIcon: {
        marginRight: 12,
    },
    cardInfo: {
        flex: 1,
    },
    cardType: {
        fontSize: Fonts.FONT_SIZE_16 ?? 16,
        fontWeight: "700",
        color: GRAY_DARK,
    },
    cardDate: {
        fontSize: Fonts.FONT_SIZE_12 ?? 12,
        color: GRAY,
        marginTop: 2,
    },
    cardFile: {
        fontSize: Fonts.FONT_SIZE_12 ?? 12,
        color: GRAY,
        marginTop: 1,
    },
    viewBtn: {
        padding: 6,
        marginRight: 4,
    },
    deleteBtn: {
        padding: 6,
    },
    addButton: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: PRIMARY,
        margin: 16,
        padding: 14,
        borderRadius: 12,
        gap: 8,
    },
    addButtonText: {
        color: "#FFF",
        fontSize: Fonts.FONT_SIZE_16 ?? 16,
        fontWeight: "700",
        marginLeft: 6,
    },
    // ---- Modal ----
    modalOverlay: {
        flex: 1,
        backgroundColor: "rgba(0,0,0,0.45)",
        justifyContent: "flex-end",
    },
    modalBox: {
        backgroundColor: "#FFF",
        borderTopLeftRadius: 24,
        borderTopRightRadius: 24,
        padding: 24,
        paddingBottom: 36,
    },
    modalTitle: {
        fontSize: Fonts.FONT_SIZE_18 ?? 18,
        fontWeight: "700",
        color: GRAY_DARK,
        marginBottom: 20,
        textAlign: "center",
    },
    label: {
        fontSize: Fonts.FONT_SIZE_14 ?? 14,
        fontWeight: "600",
        color: GRAY_DARK,
        marginBottom: 8,
    },
    typeGrid: {
        flexDirection: "row",
        flexWrap: "wrap",
        gap: 8,
        marginBottom: 20,
    },
    typeChip: {
        borderWidth: 1.5,
        borderColor: GRAY,
        borderRadius: 20,
        paddingHorizontal: 14,
        paddingVertical: 7,
    },
    typeChipSelected: {
        borderColor: PRIMARY,
        backgroundColor: PRIMARY + "15",
    },
    typeChipText: {
        fontSize: Fonts.FONT_SIZE_14 ?? 14,
        color: GRAY,
        fontWeight: "500",
    },
    typeChipTextSelected: {
        color: PRIMARY,
        fontWeight: "700",
    },
    filePicker: {
        flexDirection: "row",
        alignItems: "center",
        borderWidth: 1.5,
        borderColor: GRAY,
        borderStyle: "dashed",
        borderRadius: 10,
        padding: 14,
        marginBottom: 24,
        gap: 10,
    },
    filePickerText: {
        flex: 1,
        fontSize: Fonts.FONT_SIZE_14 ?? 14,
        color: GRAY,
    },
    filePickerSelected: {
        color: GRAY_DARK,
        fontWeight: "500",
    },
    modalActions: {
        flexDirection: "row",
        gap: 12,
    },
    cancelBtn: {
        flex: 1,
        borderWidth: 1.5,
        borderColor: GRAY,
        borderRadius: 10,
        padding: 14,
        alignItems: "center",
    },
    cancelBtnText: {
        fontSize: Fonts.FONT_SIZE_16 ?? 16,
        color: GRAY_DARK,
        fontWeight: "600",
    },
    confirmBtn: {
        flex: 1,
        backgroundColor: PRIMARY,
        borderRadius: 10,
        padding: 14,
        alignItems: "center",
    },
    confirmBtnText: {
        fontSize: Fonts.FONT_SIZE_16 ?? 16,
        color: "#FFF",
        fontWeight: "700",
    },
    // ---- Image Viewer ----
    viewerOverlay: {
        flex: 1,
        backgroundColor: "rgba(0,0,0,0.92)",
        justifyContent: "center",
        alignItems: "center",
    },
    viewerHeader: {
        position: "absolute",
        top: 48,
        left: 0,
        right: 0,
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
        paddingHorizontal: 20,
    },
    viewerTitle: {
        color: "#FFF",
        fontSize: Fonts.FONT_SIZE_18 ?? 18,
        fontWeight: "700",
    },
    viewerImage: {
        width: "100%",
        height: "80%",
    },
});
